import { useEffect, useMemo, useRef, useState } from 'react'

import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core'
import { Search, X } from 'lucide-react'

import toastError from '../../errors/toastError'
import { getInitials } from '../../helpers/getInitials'
import api from '../../services/api'
import ContactTags from '../ContactTags'
import { useStyles } from './style'

// Mesma regra de formatação usada em TicketInfo para manter o protocolo consistente.
const getTicketProtocol = ticket => {
  if (!ticket) return ''
  if (ticket.protocol) return ticket.protocol
  if (!ticket.id) return ''

  const baseDate = ticket.createdAt ? new Date(ticket.createdAt) : new Date()
  if (Number.isNaN(baseDate.getTime())) return String(ticket.id)

  const day = String(baseDate.getDate()).padStart(2, '0')
  const month = String(baseDate.getMonth() + 1).padStart(2, '0')
  const year = baseDate.getFullYear()

  return `${day}${month}${year}-${ticket.id}`
}

const getMessagePreview = message => {
  if (message.body) return message.body
  if (message.mediaType && message.mediaType !== 'chat') return `[${message.mediaType}]`
  return ''
}

const TicketReportPreviewModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles()

  const [loading, setLoading] = useState(true)
  const [ticket, setTicket] = useState(null)
  const [messages, setMessages] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (!open || !ticketId) return

    let isMounted = true
    setLoading(true)
    setSearchTerm('')

    const fetchData = async () => {
      try {
        const [ticketRes, messagesRes] = await Promise.all([
          api.get(`/tickets/${ticketId}`),
          api.get(`/messages/${ticketId}`, {
            params: { markAsRead: false, includeHistory: false }
          })
        ])

        if (!isMounted) return

        setTicket(ticketRes.data)
        setMessages(messagesRes.data?.messages || [])
      } catch (err) {
        if (isMounted) toastError(err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchData()
    return () => {
      isMounted = false
    }
  }, [open, ticketId])

  const filteredMessages = useMemo(() => {
    if (!searchTerm.trim()) return messages
    const term = searchTerm.trim().toLowerCase()
    return messages.filter(message =>
      getMessagePreview(message).toLowerCase().includes(term)
    )
  }, [messages, searchTerm])

  // Mantém a última mensagem sempre visível ao abrir ou atualizar a lista.
  useEffect(() => {
    if (!loading) {
      messagesEndRef.current?.scrollIntoView({ block: 'end' })
    }
  }, [loading, filteredMessages])

  const contact = ticket?.contact

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: classes.paperRoot }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.headerInfo}>
          <Avatar src={contact?.profilePicUrl} className={classes.avatar}>
            {getInitials(contact?.name || '')}
          </Avatar>
          <div>
            <Typography className={classes.contactName}>
              {contact?.name || '-'}
            </Typography>
            <Typography className={classes.protocol}>
              Protocolo: {getTicketProtocol(ticket)}
            </Typography>
          </div>
        </div>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={onClose}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ContactTags
          contact={contact}
          ticket={ticket}
          showAllTypes
          disabledAddTags
        />

        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Pesquisar mensagens neste atendimento"
          className={classes.searchField}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            )
          }}
        />

        <div className={classes.messagesList}>
          {loading ? (
            <Typography className={classes.emptyText}>Carregando...</Typography>
          ) : filteredMessages.length === 0 ? (
            <Typography className={classes.emptyText}>
              Nenhuma mensagem encontrada.
            </Typography>
          ) : (
            filteredMessages.map(message => (
              <div
                key={message.id}
                className={
                  message.fromMe
                    ? classes.messageItemRight
                    : classes.messageItemLeft
                }
              >
                <Typography className={classes.messageMeta}>
                  {message.fromMe ? 'Atendente' : contact?.name || 'Contato'}
                  {' · '}
                  {new Date(message.createdAt).toLocaleString('pt-BR')}
                </Typography>
                <Typography className={classes.messageBody}>
                  {getMessagePreview(message) || '-'}
                </Typography>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TicketReportPreviewModal
